import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CloudOff, Loader2, Sparkles } from "lucide-react";
import MarketDetailOrderModal from "../components/bond-predict/detail/MarketDetailOrderModal";
import PredictChrome from "../components/bond-predict/PredictChrome";
import PredictFiltersRail from "../components/bond-predict/PredictFiltersRail";
import PredictPulseTape from "../components/bond-predict/PredictPulseTape";
import PredictMarketCard from "../components/bond-predict/PredictMarketCard";
import PredictMarketInspector from "../components/bond-predict/PredictMarketInspector";
import { selectAppUser, checkAppAuth } from "../redux/appAuthSlice";
import {
  fetchBondPredictLeagues,
  fetchBondPredictMarkets,
  fetchBondPredictPortfolio,
  fetchBondPredictTrending,
  selectBondPredictLeagues,
  selectBondPredictLikeBusyId,
  selectBondPredictMarkets,
  selectBondPredictMarketsError,
  selectBondPredictMarketsStatus,
  selectBondPredictOrderBook,
  selectBondPredictOrderBookStatus,
  selectBondPredictTrending,
  toggleBondPredictLike,
} from "../redux/bondPredictSlice";
import {
  buildFetchParams,
  clientFilterMarkets,
  extractLeaguesFromMarkets,
  isMarketLikedByUser,
  mergeLeagueOptions,
  sortMarketsClient,
} from "../utils/bondPredictHelpers";
import { usePersistedPanelSize } from "../hooks/usePersistedPanelSize";
import { isOutcomeTradable } from "../utils/bondPredictDetailHelpers";
import { ORDER_SIDES } from "../utils/bondPredictTradeHelpers";
import "../bondhouse.css";
import "../bond-predict.css";
import "../bond-predict-trade-modal.css";

const PORTFOLIO_PATH = "/app/bond-predict/portfolio";
const FILTER_RAIL_DEFAULT = 340;
const FILTER_RAIL_MIN = 68;
const FILTER_RAIL_MAX = 560;
const FILTER_RAIL_COMPACT = 120;

const BondPredict = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectAppUser);
  const orderBook = useSelector(selectBondPredictOrderBook);
  const orderBookStatus = useSelector(selectBondPredictOrderBookStatus);
  const markets = useSelector(selectBondPredictMarkets);
  const trending = useSelector(selectBondPredictTrending);
  const apiLeagues = useSelector(selectBondPredictLeagues);
  const marketsStatus = useSelector(selectBondPredictMarketsStatus);
  const marketsError = useSelector(selectBondPredictMarketsError);
  const likeBusyId = useSelector(selectBondPredictLikeBusyId);

  const [status, setStatus] = useState("");
  const [type, setType] = useState("");
  const [category, setCategory] = useState("");
  const [league, setLeague] = useState("");
  const [sortKey, setSortKey] = useState("volume");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [inspectorOpen, setInspectorOpen] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [toast, setToast] = useState(null);
  const [tradeModalOpen, setTradeModalOpen] = useState(false);
  const [tradeInitialSide, setTradeInitialSide] = useState(ORDER_SIDES.BUY);
  const [tradeMarket, setTradeMarket] = useState(null);
  const [tradeOutcome, setTradeOutcome] = useState(null);
  const {
    size: filterRailWidth,
    setClampedSize: setFilterRailWidth,
    reset: resetFilterRailWidth,
    minSize: filterRailMin,
    maxSize: filterRailMax,
  } = usePersistedPanelSize("bb-bp-filter-rail-w", {
    defaultSize: FILTER_RAIL_DEFAULT,
    minSize: FILTER_RAIL_MIN,
    maxSize: FILTER_RAIL_MAX,
  });
  const isFilterRailCollapsed = filterRailWidth <= FILTER_RAIL_COMPACT;

  const toggleFilterRailWidth = useCallback(() => {
    if (isFilterRailCollapsed) resetFilterRailWidth();
    else setFilterRailWidth(FILTER_RAIL_MIN);
  }, [isFilterRailCollapsed, resetFilterRailWidth, setFilterRailWidth]);

  useEffect(() => {
    if (!user?._id) return;
    dispatch(fetchBondPredictPortfolio({ limit: 100, skip: 0 }));
  }, [user?._id, dispatch]);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedSearch(search.trim()), 320);
    return () => window.clearTimeout(t);
  }, [search]);

  const activeFilterCount = [status, type, category, league].filter(Boolean).length;

  const leagueOptions = useMemo(
    () => mergeLeagueOptions(apiLeagues, extractLeaguesFromMarkets(markets)),
    [apiLeagues, markets]
  );

  const load = useCallback(async () => {
    const params = buildFetchParams({
      status,
      type,
      category,
      league,
      search: debouncedSearch,
      sortKey,
    });
    await Promise.all([
      dispatch(fetchBondPredictMarkets(params)),
      dispatch(fetchBondPredictTrending(8)),
      dispatch(fetchBondPredictLeagues()),
    ]);
  }, [dispatch, status, type, category, league, debouncedSearch, sortKey]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(t);
  }, [toast]);

  const displayMarkets = useMemo(() => {
    let list = clientFilterMarkets(markets, search && search !== debouncedSearch ? search : "");
    return sortMarketsClient(list, sortKey);
  }, [markets, search, debouncedSearch, sortKey]);

  useEffect(() => {
    if (!displayMarkets.length) {
      setSelectedId(null);
      return;
    }
    const stillVisible = selectedId && displayMarkets.some((m) => m._id === selectedId);
    if (!stillVisible) {
      setSelectedId(displayMarkets[0]._id);
    }
  }, [displayMarkets, selectedId]);

  const selectedMarket = useMemo(
    () => displayMarkets.find((m) => m._id === selectedId) || null,
    [displayMarkets, selectedId]
  );

  const liveMarketCount = useMemo(
    () =>
      displayMarkets.filter(
        (market) => String(market?.status || "").toUpperCase() === "OPEN"
      ).length,
    [displayMarkets]
  );

  const loading = marketsStatus === "loading" && !refreshing;

  const handleSelectMarket = (market) => {
    if (!market?._id) return;
    setSelectedId(market._id);
    setInspectorOpen(true);
  };

  const handleCloseInspector = () => {
    setInspectorOpen(false);
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  };

  const handleClearFilters = () => {
    setStatus("");
    setType("");
    setCategory("");
    setLeague("");
    setSortKey("volume");
  };

  const handleToggleLike = async (market) => {
    if (!user?._id) {
      setToast({ tone: "warn", text: "Sign in to like markets." });
      return;
    }
    const isLiked = isMarketLikedByUser(market, user._id);
    const result = await dispatch(
      toggleBondPredictLike({ marketId: market._id, isLiked, userId: user._id })
    );
    if (toggleBondPredictLike.rejected.match(result)) {
      setToast({ tone: "error", text: result.payload || "Could not update like" });
    }
  };

  const handleOpenMarket = (market) => {
    if (market?._id) navigate(`/app/bond-predict/${market._id}`);
  };

  const handleOutcomeClick = (outcome, market) => {
    if (!user?._id) {
      setToast({ tone: "warn", text: "Sign in to place orders." });
      return;
    }
    const targetMarket = market || selectedMarket;
    if (!targetMarket?._id || !outcome?._id) return;
    if (!isOutcomeTradable(targetMarket, outcome)) {
      setToast({ tone: "error", text: "Trading is not available for this outcome." });
      return;
    }
    setTradeMarket(targetMarket);
    setTradeOutcome(outcome);
    setTradeInitialSide(ORDER_SIDES.BUY);
    setTradeModalOpen(true);
  };

  const handleTradePlaced = async (toastPayload) => {
    if (toastPayload) setToast(toastPayload);
    await load();
    dispatch(checkAppAuth());
    if (user?._id) {
      dispatch(fetchBondPredictPortfolio({ limit: 100, skip: 0 }));
    }
  };

  const handleFilterRailResizePointerDown = useCallback(
    (event) => {
      event.preventDefault();
      const startX = event.clientX;
      const startSize = filterRailWidth;
      document.body.classList.add("ws-is-resizing");

      const onMove = (ev) => {
        setFilterRailWidth(startSize + (ev.clientX - startX));
      };

      const onUp = () => {
        document.body.classList.remove("ws-is-resizing");
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        window.removeEventListener("pointercancel", onUp);
      };

      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
      window.addEventListener("pointercancel", onUp);
    },
    [filterRailWidth, setFilterRailWidth]
  );

  return (
    <div
      className={`bp-page${isFilterRailCollapsed ? " bp-filter-rail-collapsed" : ""}`}
      style={{ "--bp-filters-w": `${filterRailWidth}px`, "--floor-index-w": `${filterRailWidth}px` }}
    >
      {toast ? (
        <div className={`bp-toast bp-toast-${toast.tone || "info"}`} role="status">
          {toast.text}
        </div>
      ) : null}

      <div className="bp-desk-layout">
        <div className="floor-index-pane bp-rail-column" aria-label="Market filters">
          <PredictFiltersRail
            status={status}
            type={type}
            category={category}
            league={league}
            leagues={leagueOptions}
            sortKey={sortKey}
            search={search}
            onSearchChange={setSearch}
            onStatusChange={setStatus}
            onTypeChange={setType}
            onCategoryChange={setCategory}
            onLeagueChange={setLeague}
            onSortChange={setSortKey}
            onClear={handleClearFilters}
            onRefresh={handleRefresh}
            refreshing={refreshing}
            isLoading={loading}
            activeCount={activeFilterCount}
            resultCount={displayMarkets.length}
            totalCount={markets.length}
            liveCount={liveMarketCount}
            collapsed={isFilterRailCollapsed}
            onToggleCollapse={toggleFilterRailWidth}
          />
          <div
            className="floor-resize-handle"
            role="separator"
            aria-orientation="vertical"
            aria-valuenow={filterRailWidth}
            aria-valuemin={filterRailMin}
            aria-valuemax={filterRailMax}
            aria-label="Resize market filters"
            title="Drag to resize · double-click to reset"
            onPointerDown={handleFilterRailResizePointerDown}
            onDoubleClick={resetFilterRailWidth}
          />
        </div>

        <div className="bp-main-column">
          <PredictChrome
            search={search}
            onSearchChange={setSearch}
            refreshing={refreshing}
            onRefresh={handleRefresh}
            inspectorOpen={inspectorOpen}
            onInspectorToggle={() => setInspectorOpen((v) => !v)}
            onPortfolio={() => navigate(PORTFOLIO_PATH)}
            filtersOpen={filtersOpen}
            onFiltersToggle={() => setFiltersOpen((v) => !v)}
            activeFilterCount={activeFilterCount}
          />

          <div className="bp-desk-main">
            <PredictPulseTape
              markets={trending?.length ? trending : displayMarkets}
              onSelect={handleSelectMarket}
            />

            <div className={`bp-board-wrap${inspectorOpen ? "" : " bp-board-wrap-solo"}`}>
          <main className="bp-board">
            {loading ? (
              <div className="bp-state bp-state-loading">
                <Loader2 size={28} className="bp-spin" />
                <p>Scanning the forecast desk…</p>
              </div>
            ) : marketsError ? (
              <div className="bp-state bp-state-error">
                <CloudOff size={28} />
                <p>{marketsError}</p>
                <button type="button" className="bp-primary-btn" onClick={handleRefresh}>
                  Try again
                </button>
              </div>
            ) : displayMarkets.length === 0 ? (
              <div className="bp-state bp-state-empty">
                <Sparkles size={28} />
                <h2>No markets match</h2>
                <p>Try clearing filters or check back when new contracts go live.</p>
                {activeFilterCount ? (
                  <button type="button" className="bp-primary-btn" onClick={handleClearFilters}>
                    Clear filters
                  </button>
                ) : null}
              </div>
            ) : (
              <div className="bp-grid">
                {displayMarkets.map((market) => (
                  <PredictMarketCard
                    key={market._id}
                    market={market}
                    active={selectedId === market._id}
                    isLiked={isMarketLikedByUser(market, user?._id)}
                    likeBusy={likeBusyId === market._id}
                    onSelect={handleSelectMarket}
                    onToggleLike={handleToggleLike}
                    onOutcomeClick={handleOutcomeClick}
                    userId={user?._id}
                  />
                ))}
              </div>
            )}
          </main>

          {inspectorOpen ? (
            <PredictMarketInspector
              market={selectedMarket}
              isLiked={selectedMarket ? isMarketLikedByUser(selectedMarket, user?._id) : false}
              likeBusy={selectedMarket && likeBusyId === selectedMarket._id}
              onClose={handleCloseInspector}
              onToggleLike={handleToggleLike}
              onOpenMarket={handleOpenMarket}
              onOutcomeClick={handleOutcomeClick}
              userId={user?._id}
            />
          ) : null}
            </div>
          </div>
        </div>
      </div>

      {inspectorOpen && selectedMarket ? (
        <button
          type="button"
          className="bp-inspector-sheet-backdrop"
          onClick={handleCloseInspector}
          aria-label="Close market preview"
        />
      ) : null}

      {filtersOpen ? (
        <div className="bp-filters-drawer-backdrop" onClick={() => setFiltersOpen(false)} role="presentation">
          <div
            className="bp-filters-drawer"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-label="Market filters"
          >
            <PredictFiltersRail
              compact
              status={status}
              type={type}
              category={category}
              league={league}
              leagues={leagueOptions}
              sortKey={sortKey}
              search={search}
              onSearchChange={setSearch}
              onStatusChange={setStatus}
              onTypeChange={setType}
              onCategoryChange={setCategory}
              onLeagueChange={setLeague}
              onSortChange={setSortKey}
              onClear={handleClearFilters}
              onRefresh={handleRefresh}
              refreshing={refreshing}
              isLoading={loading}
              activeCount={activeFilterCount}
              resultCount={displayMarkets.length}
              totalCount={markets.length}
              liveCount={liveMarketCount}
              onClose={() => setFiltersOpen(false)}
            />
          </div>
        </div>
      ) : null}

      <MarketDetailOrderModal
        open={tradeModalOpen}
        onClose={() => setTradeModalOpen(false)}
        market={tradeMarket}
        selectedOutcome={tradeOutcome}
        orderBook={orderBook}
        orderBookStatus={orderBookStatus}
        userCoin={user?.COIN}
        initialSide={tradeInitialSide}
        onPlaced={handleTradePlaced}
      />
    </div>
  );
};

export default BondPredict;
